import React, { Fragment, useMemo } from 'react';

import { Box, Divider } from '@mui/material';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { columnStyles, dividerStyles, getContainerStyles } from './HeaderGrid.styles';
import { getGridRowCount } from './HeaderGrid.utils';
import HeaderGridCell from './HeaderGridCell';

const defaultGetItemKey = item => item.id;

const HeaderGrid = ({
  columns,
  renderItem,
  rowCount = null,
  containerSx = null,
  columnGap = null,
  getItemKey = defaultGetItemKey,
  hasEqualColumns = false,
}) => {
  const resolvedRowCount = rowCount ?? getGridRowCount(columns);

  const memoizedColumnStyles = useMemo(
    () => columnStyles({ rowCount: resolvedRowCount, hasEqualColumns }),
    [resolvedRowCount, hasEqualColumns],
  );

  const memoizedContainerSx = useMemo(() => {
    const baseContainerStyles = getContainerStyles(_.isNil(columnGap) ? undefined : { columnGap });

    return containerSx ? { ...baseContainerStyles, ...containerSx } : baseContainerStyles;
  }, [columnGap, containerSx]);

  return (
    <Box sx={memoizedContainerSx}>
      {columns.map((column, columnDisplayIndex) => {
        const isLastColumn = columnDisplayIndex === columns.length - 1;
        const showDivider = column.hasDividerAfter && !isLastColumn;

        return (
          <Fragment key={column.columnIndex}>
            <Box sx={memoizedColumnStyles}>
              {column.cells.map(cell => (
                <HeaderGridCell
                  cell={cell}
                  getItemKey={getItemKey}
                  key={`${column.columnIndex}-${cell.rowIndex}`}
                  renderItem={renderItem}
                />
              ))}
            </Box>
            {showDivider && <Divider flexItem orientation="vertical" sx={dividerStyles} />}
          </Fragment>
        );
      })}
    </Box>
  );
};

const headerCellShape = PropTypes.shape({
  rowIndex: PropTypes.number.isRequired,
  items: PropTypes.array.isRequired,
});

const headerColumnShape = PropTypes.shape({
  columnIndex: PropTypes.number.isRequired,
  cells: PropTypes.arrayOf(headerCellShape).isRequired,
  hasDividerAfter: PropTypes.bool,
});

HeaderGrid.propTypes = {
  columns: PropTypes.arrayOf(headerColumnShape).isRequired,
  renderItem: PropTypes.func.isRequired,
  containerSx: PropTypes.oneOfType([PropTypes.object, PropTypes.array]),
  getItemKey: PropTypes.func,
  hasEqualColumns: PropTypes.bool,
  rowCount: PropTypes.number,
  columnGap: PropTypes.number,
};

export default HeaderGrid;
